/**
 * O token de acesso desta aba, renovado se estiver na virada.
 *
 * ── Por que isto é uma função só, e não uma por chamador ──────────────────
 *
 * Ela nasceu dentro do `criarClienteDaSessao`, e o fluxo de eventos escreveu a
 * própria versão — parecida, mas sem a renovação. O resultado foi um defeito
 * que ninguém via: com o token vencido, o fluxo mandava o mesmo token morto a
 * cada reconexão, para sempre. O servidor recusava com 401, o recuo tentava de
 * novo, e a tela ficava sem tempo real sem nada indicar isso.
 *
 * Medido em 02/09/2026 na staging: 982 recusas contra 17 conexões em seis
 * horas. Mensagem recebida não aparecia na conversa aberta, e quem atendia
 * descobria trocando de conversa.
 *
 * O comentário do `fluxo-sse.ts` dizia que *"o `token()` da próxima volta já
 * renova"*. Era a intenção, e nunca foi o código daquele lado — quem renovava
 * era só o cliente da API. Enquanto alguém clicava, o token guardado era
 * renovado por tabela e o fluxo pegava carona; parado, ele nunca mais subia. É
 * por isso que o defeito parecia intermitente.
 *
 * Duas cópias de uma regra é o bastante para uma delas ficar para trás. Agora é
 * uma.
 *
 * ── Renovar ANTES, e não depois do 401 ────────────────────────────────────
 *
 * Se falta menos de um minuto, renova antes de devolver. Assim a requisição sai
 * com token bom, em vez de sair, tomar 401 e ser refeita — o que dobraria a
 * latência justamente na virada.
 */

import { ler, precisaRenovar } from './cofre';
import { renovarSessao } from './fluxo-oidc';

export class SemSessaoError extends Error {
  constructor() {
    super('Não há sessão válida para chamar a API.');
    this.name = 'SemSessaoError';
  }
}

export async function tokenDaSessao(revenda: string | null): Promise<string> {
  if (precisaRenovar() && !(await renovarSessao(revenda))) throw new SemSessaoError();

  const atual = ler();
  if (!atual) throw new SemSessaoError();
  return atual.acesso;
}
